import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { useOutletContext } from "react-router-dom";
import toast from "react-hot-toast";

import PermissionGuard from "@/permissions/PermissionGuard";
import ModuleUnavailableNotice from "@/permissions/ModuleUnavailableNotice";
import { MODULES } from "@/permissions/modules";
import { PERMISSIONS } from "@/permissions/constant";
import { ComplianceService } from "@/services/complianceService";

import DateRangeBar from "./components/DateRangeBar";
import AttentionStrip from "./components/AttentionStrip";
import OverviewSection from "./components/OverviewSection";
import BottlenecksSection from "./components/BottlenecksSection";
import ReviewersSection from "./components/ReviewersSection";
import AiAccuracySection from "./components/AiAccuracySection";
import QualitySection from "./components/QualitySection";

// Maps an attention-strip item's `family` to the section it should scroll to
// when clicked — the strip is a triage jump-off point, not a standalone view.
const FAMILY_ANCHOR = {
  overview: "analytics-overview",
  bottlenecks: "analytics-bottlenecks",
  reviewers: "analytics-reviewers",
  aiAccuracy: "analytics-ai-accuracy",
  quality: "analytics-quality",
};

// Compliance Analytics (Phase 2B). A single scrollable leadership page, five
// metric families top→bottom: process health → bottlenecks → reviewer activity
// → AI accuracy → checklist quality. Shared date-range header; each family
// fetches independently (a slow/failed one never blocks the others) and exports
// its own CSV. Chart form is chosen per data: one time-series (F1 area line),
// horizontal bars for magnitude/rank (F2/F4/F5), a ranked table with inline bars
// (F3), a radial gauge for the AI trust headline (F4), and donuts only for true
// part-of-whole splits (F5). Built on the project's tokens + the 2A chart
// vocabulary so it reads as the same product.

const DEFAULT_PRESET = "30";
const initialRange = () => {
  const to = new Date();
  const from = new Date();
  from.setDate(from.getDate() - 29);
  const ymd = (d) => d.toISOString().slice(0, 10);
  return { from: ymd(from), to: ymd(to) };
};

const ComplianceAnalytics = () => {
  const setHeader = useOutletContext();

  const [range, setRange] = useState(initialRange);
  const [preset, setPreset] = useState(DEFAULT_PRESET);

  const [attention, setAttention] = useState(null);
  const [overview, setOverview] = useState(null);
  const [bottlenecks, setBottlenecks] = useState(null);
  const [reviewers, setReviewers] = useState(null);
  const [aiAccuracy, setAiAccuracy] = useState(null);
  const [quality, setQuality] = useState(null);

  const [loading, setLoading] = useState({
    attention: true,
    overview: true,
    bottlenecks: true,
    reviewers: true,
    aiAccuracy: true,
    quality: true,
  });

  useEffect(() => {
    setHeader({ title: "Compliance", subTitle: "Analytics" });
    return () => setHeader({ title: "", subTitle: "" });
  }, [setHeader]);

  // Re-fetch every family whenever the window changes. Each is independent and
  // degrades to null (→ the section renders its own empty state) on failure.
  useEffect(() => {
    let active = true;
    const run = (key, promise, setter) => {
      setLoading((l) => ({ ...l, [key]: true }));
      promise
        .then((data) => active && setter(data ?? null))
        .catch(() => active && setter(null))
        .finally(() => active && setLoading((l) => ({ ...l, [key]: false })));
    };

    run("attention", ComplianceService.getAnalyticsAttention(range), setAttention);
    run("overview", ComplianceService.getAnalyticsOverview(range), setOverview);
    run("bottlenecks", ComplianceService.getAnalyticsBottlenecks(range), setBottlenecks);
    run("reviewers", ComplianceService.getAnalyticsReviewers(range), setReviewers);
    run("aiAccuracy", ComplianceService.getAnalyticsAiAccuracy(range), setAiAccuracy);
    run("quality", ComplianceService.getAnalyticsQuality(range), setQuality);

    return () => {
      active = false;
    };
  }, [range]);

  // Which view is mid-download, so its two buttons disable together. A PDF is
  // rendered server-side and takes longer than a CSV; without this the officer
  // clicks again, and a second identical report starts building.
  const [exportingView, setExportingView] = useState(null);

  const exportAs = useCallback(
    (format) => (view) => async () => {
      setExportingView(view);
      try {
        if (format === "pdf") {
          await ComplianceService.exportAnalyticsPdf(view, range);
        } else {
          await ComplianceService.exportAnalyticsCsv(view, range);
        }
      } catch {
        toast.error(`Could not export ${format.toUpperCase()}. Please try again.`);
      } finally {
        setExportingView(null);
      }
    },
    [range],
  );

  const exportCsv = useMemo(() => exportAs("csv"), [exportAs]);
  const exportPdf = useMemo(() => exportAs("pdf"), [exportAs]);

  // Jump target for the attention strip — scrolls to the section that owns
  // the fired trigger rather than navigating away from the page.
  const sectionRefs = useRef({});
  const jumpToFamily = useCallback((family) => {
    const id = FAMILY_ANCHOR[family];
    const el = id && sectionRefs.current[id];
    el?.scrollIntoView({ behavior: "smooth", block: "start" });
  }, []);

  return (
    <PermissionGuard permission={PERMISSIONS.COMPLIANCE_VIEW}>
      <div className="flex flex-col gap-4" style={{ zoom: 0.9 }}>
        {/* This page writes nothing — every family is a GET and so is each CSV
            export, which is why they all stay enabled. The notice is still here:
            a reader comparing yesterday's numbers with today's needs to know
            that no new document has been taken in since the kill. */}
        <ModuleUnavailableNotice module={MODULES.COMPLIANCE} suffix="Nothing on this page is affected — every figure and export still works." />

        {/* Shared window control */}
        <div className="flex items-center justify-between flex-wrap gap-2">
          <DateRangeBar value={range} onChange={setRange} activePreset={preset} onPresetChange={setPreset} />
        </div>

        {/* Cross-family attention strip — a 30-second triage view pinned
            above Family 1 (added 2026-08-01). */}
        <AttentionStrip data={attention} loading={loading.attention} onJump={jumpToFamily} />

        {/* F1 → F5 top to bottom */}
        <div ref={(el) => (sectionRefs.current["analytics-overview"] = el)}>
          <OverviewSection
            data={overview}
            loading={loading.overview}
            onExportCsv={exportCsv("overview")}
            onExportPdf={exportPdf("overview")}
            exporting={exportingView === "overview"}
          />
        </div>
        <div ref={(el) => (sectionRefs.current["analytics-bottlenecks"] = el)}>
          <BottlenecksSection
            data={bottlenecks}
            loading={loading.bottlenecks}
            onExportCsv={exportCsv("bottlenecks")}
            onExportPdf={exportPdf("bottlenecks")}
            exporting={exportingView === "bottlenecks"}
          />
        </div>
        <div ref={(el) => (sectionRefs.current["analytics-reviewers"] = el)}>
          <ReviewersSection
            data={reviewers}
            loading={loading.reviewers}
            onExportCsv={exportCsv("reviewers")}
            onExportPdf={exportPdf("reviewers")}
            exporting={exportingView === "reviewers"}
          />
        </div>
        <div ref={(el) => (sectionRefs.current["analytics-ai-accuracy"] = el)}>
          <AiAccuracySection
            data={aiAccuracy}
            loading={loading.aiAccuracy}
            onExportCsv={exportCsv("ai-accuracy")}
            onExportPdf={exportPdf("ai-accuracy")}
            exporting={exportingView === "ai-accuracy"}
          />
        </div>
        <div ref={(el) => (sectionRefs.current["analytics-quality"] = el)}>
          <QualitySection
            data={quality}
            loading={loading.quality}
            onExportCsv={exportCsv("quality")}
            onExportPdf={exportPdf("quality")}
            exporting={exportingView === "quality"}
          />
        </div>
      </div>
    </PermissionGuard>
  );
};

export default ComplianceAnalytics;
